import {
  DataTable,
  DEFAULT_DEBOUNCE_DELAY,
  MainButton,
  Option,
  STATUSPAGE,
} from '@repo/ui';
import debounce from 'debounce';
import { GET_PAGINATED_ORGANIZATION_ACTIVE_TCES_COLUMNS } from 'domain/models';
import { GetPaginatedOrganizationActiveTcesUseCaseInterface } from 'domain/usecases';
import FeatherIcon from 'feather-icons-react';
import { RequestState } from 'presentation/app/common/interfaces/request-state';
import { notifyError } from 'presentation/app/components/notify';
import { useDataTableRemotePagination } from 'presentation/app/hooks/use-datatable-remote-pagination';
import { useService } from 'presentation/app/hooks/useService';
import { makeActiveTcesColumns } from 'presentation/app/modules/Entities/common/columns/entity-settings/active-tces-columns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';

import noDataBg from '../../../../../../../assets/images/no-task.png';
import { CourseAndGroupFilters } from '../../../../course-and-group-filters';

import { TcesTabSectionId } from '..';

interface IProps {
  entityId: string;
  changeSelectedTcesTabSectionId: (sectionId: TcesTabSectionId) => void;
  courseOptions: Option[];
  groupOptions: Option[];
  isCourseSelectLoading?: boolean;
  isGroupSelectLoading?: boolean;
}

export function ActiveTcesSection({
  entityId,
  changeSelectedTcesTabSectionId,
  courseOptions,
  groupOptions,
  isCourseSelectLoading,
  isGroupSelectLoading,
}: IProps) {
  // Hooks
  const { entitiesService } = useService();
  const {
    paginationMetadata,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearchRemote,
    handleSortRemote,
  } = useDataTableRemotePagination<
    GetPaginatedOrganizationActiveTcesUseCaseInterface.Result['tces'][number],
    GET_PAGINATED_ORGANIZATION_ACTIVE_TCES_COLUMNS
  >({
    initialColumnToOrderBy: GET_PAGINATED_ORGANIZATION_ACTIVE_TCES_COLUMNS.NAME,
  });

  // States
  const [
    getPaginatedOrganizationActiveTcesRequest,
    setGetPaginatedOrganizationActiveTcesRequest,
  ] = useState<
    RequestState<GetPaginatedOrganizationActiveTcesUseCaseInterface.Result>
  >({
    data: undefined,
    status: STATUSPAGE.INITIAL,
  });
  const [selectedCourses, setSelectedCourses] = useState<Option | Option[]>(
    courseOptions.length === 1 ? courseOptions[0] : [],
  );
  const [selectedGroups, setSelectedGroups] = useState<Option | Option[]>(
    groupOptions.length === 1 ? groupOptions[0] : [],
  );

  // Methods
  const requestGetPaginatedOrganizationActiveTces = useCallback(
    (params: GetPaginatedOrganizationActiveTcesUseCaseInterface.Params) => {
      setGetPaginatedOrganizationActiveTcesRequest((prevState) => ({
        ...prevState,
        status: STATUSPAGE.LOADING,
      }));
      entitiesService
        .getPaginatedOrganizationActiveTces(params)
        .then((res) => {
          setGetPaginatedOrganizationActiveTcesRequest((prevState) => ({
            ...prevState,
            data: res,
          }));
        })
        .catch((err) => {
          setGetPaginatedOrganizationActiveTcesRequest((prevState) => ({
            ...prevState,
            data: undefined,
          }));
          notifyError(err?.message || 'Erro ao buscar rascunhos de TCE');
        })
        .finally(() => {
          setGetPaginatedOrganizationActiveTcesRequest((prevState) => ({
            ...prevState,
            status: STATUSPAGE.DONE,
          }));
        });
    },
    [entityId],
  );

  const debouncedRequestGetPaginatedOrganizationActiveTces = useMemo(
    () =>
      debounce(
        (
          valuesToSubmit: GetPaginatedOrganizationActiveTcesUseCaseInterface.Params,
        ) => {
          requestGetPaginatedOrganizationActiveTces(valuesToSubmit);
        },
        DEFAULT_DEBOUNCE_DELAY,
      ),
    [],
  );

  useEffect(() => {
    const valuesToSubmit = {
      organizationHashId: entityId,
      courseHashIds: Array.isArray(selectedCourses)
        ? selectedCourses.map((selectedCourse) => selectedCourse.value)
        : [selectedCourses.value],
      groupHashIds: Array.isArray(selectedGroups)
        ? selectedGroups.map((selectedGroup) => selectedGroup.value)
        : [selectedGroups.value],
      page: paginationMetadata.selectedPage,
      length: paginationMetadata.rowsPerPage,
      orderDirection: paginationMetadata.sortDirection,
      orderColumn: paginationMetadata.orderColumn,
      search: encodeURIComponent(paginationMetadata.searchString),
    };

    setGetPaginatedOrganizationActiveTcesRequest((prevState) => ({
      ...prevState,
      status: STATUSPAGE.LOADING,
    }));

    debouncedRequestGetPaginatedOrganizationActiveTces(valuesToSubmit);
  }, [entityId, paginationMetadata, selectedCourses, selectedGroups]);

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">TCEs Ativos</h5>
        <MainButton
          color="light"
          onClick={() => {
            changeSelectedTcesTabSectionId('summaries');
          }}
        >
          <FeatherIcon icon="arrow-left" size={13} />
          Voltar
        </MainButton>
      </CardHeader>
      <CardBody>
        <Row>
          <CourseAndGroupFilters
            courses={courseOptions}
            groups={groupOptions}
            isCourseSelectMulti={true}
            isGroupSelectMulti={true}
            courseSelectValue={selectedCourses}
            groupSelectValue={selectedGroups}
            isCourseSelectLoading={isCourseSelectLoading}
            isGroupSelectLoading={isGroupSelectLoading}
            onCourseChange={(course) => {
              setSelectedCourses(course);
            }}
            onGroupChange={(group) => {
              setSelectedGroups(group);
            }}
          />
        </Row>
        <DataTable
          noDataBg={noDataBg}
          data={getPaginatedOrganizationActiveTcesRequest.data?.tces || []}
          actions={[]}
          columns={makeActiveTcesColumns()}
          loading={
            getPaginatedOrganizationActiveTcesRequest.status ===
            STATUSPAGE.LOADING
          }
          pagination
          paginationServer
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          canSearch
          searchServer
          onSearch={handleSearchRemote}
          onSort={handleSortRemote}
          paginationTotalRows={
            getPaginatedOrganizationActiveTcesRequest.data?.total ?? 0
          }
          defaultSortAsc={true}
          defaultSortFieldId={1}
        />
      </CardBody>
    </Card>
  );
}
